import classNames from "classnames";

import {MetricSeries, MetricSeriesAnalysis} from "@/lib/types/metricsExplorer.ts";
import {buildFigures} from "@/pages/metricsExplorer/analysis/analysisFigures.ts";

import styles from "./AnalysisReadout.module.scss";

interface AnalysisReadoutProps {
  analysis: MetricSeriesAnalysis[];
  series: MetricSeries[];
  /** Builder row letters by series ref, matching the legend; omitted for a single series. */
  seriesLetters?: Record<string, string>;
}

/**
 * The scalars the analysis engine produced, shown with their units and window.
 *
 * These are the same figures an alert rule will threshold on, so they are presented as
 * numbers to reason about rather than as chart decoration.
 */
export const AnalysisReadout = ({analysis, series, seriesLetters}: AnalysisReadoutProps) => {
  const seriesByKey = new Map(series.map((item) => [item.key, item]));

  return (
    <div className={styles.panel}>
      <span className={styles.heading}>Results</span>
      <span className={styles.note}>
        Computed server-side over the selected window. An alert rule can threshold on any of these.
      </span>
      {analysis.map((entry) => {
        const source = seriesByKey.get(entry.series_key);
        const unit = source?.unit ?? null;
        const letter = source && seriesLetters?.[source.ref];
        return (
          <div className={styles.series} key={entry.series_key}>
            <div className={styles.seriesName}>
              {letter && `${letter} · `}
              {source?.label ?? entry.series_key}
              {source?.dimensions.map((dimension) => ` · ${dimension.value}`).join("")}
            </div>
            <div className={styles.figures}>
              {buildFigures(entry, unit).map((figure) => (
                <div key={figure.label}>
                  <span className={styles.figureLabel}>{figure.label}</span>
                  <span
                    className={classNames(styles.figureValue, {
                      [styles.up]: figure.tone === "up",
                      [styles.down]: figure.tone === "down",
                    })}
                  >
                    {figure.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
